import { useCallback, useEffect, useRef, useState } from "react"
import { Form, Formik, useFormikContext, type FormikProps } from "formik"
import { makeStyles } from "tss-react/mui"
import { Skeleton, Stack, Typography } from "@mui/material"

import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import type { MonetaryAmount } from "../../../../../modules/currency/currencyTypes"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../modules/localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { UserType } from "../../../../../modules/login/loginTypes"
import {
    MerchantMeetingServiceOrdersQuery,
    updatePartialMeetingServiceOrder,
} from "../../../../../modules/merchant/merchant/production/meetingService/meetingServiceApi"
import { MeetingServiceQuery } from "../../../../../modules/nonAdminUser/reports/home/meetingServiceApi"
import { ServiceCenterHomeQuery } from "../../../../../modules/serviceCenter/report/home/serviceCenterHomeApi"
import { ServiceCenterUpcomingMeetingsQuery } from "../../../../../modules/serviceCenter/report/upcomingBookings/upcomingMeetingsApi"
import { ConfirmModal } from "../../../dialogs/ConfirmModal"
import {
    ConfigQuery,
    fetchMeetingServiceConfiguration,
    type MeetingServiceConfigurationResponse,
} from "../../createOrderApi"
import type { SelectedPriceListItem } from "../../createOrderTypes"
import { canOverrideClosingDates } from "../../helpers"
import { useCanSetProductWishPrice } from "../../hooks/useCanSetProductWishPrice"
import { useCreateOrderDialogType } from "../../hooks/useCreateOrderDialogType"
import { useCreateOrderFormConfig } from "../../mainForm/CreateOrderFormContext"
import {
    DynamicConfigKey,
    isProductWishOrderLine,
    type DeliveryTimesField,
    type DynamicMainFormValues,
} from "../form/formTypes"
import { DeadlineMessageDialog } from "../receipt/DeadlineMessageDialog"
import type { CreateUpdateDeliveryFormValues } from "../shop/delivery/CreateUpdateDeliveryTimePopover"
import { DeadlineMessageQuery, fetchDeadlineMessage, type DeadlineMessageResponse } from "../shop/delivery/deliveryApi"
import { DeliveryTimeFields } from "../shop/delivery/components/form/DeliveryTimeFields"
import {
    buildDeliveryTimesSchema,
    DAY_OF_WEEK_NUMBER,
    earliestDeliveryTime,
    prepareInitialValues,
    sortedDeliveryTimes,
} from "../shop/delivery/helpers"

import type { BookingMove } from "./bookingMoveDeliverySyncHelpers"
import { formatDeliverySummary, toPartialDeliveries } from "./bookingMoveDeliverySyncHelpers"

const useStyles = makeStyles()((theme) => ({
    // ConfirmModal centres its children, which would otherwise shrink both blocks to their content.
    content: {
        width: "100%",
    },
    summary: {
        color: theme.palette.tailwind.slate[700],
    },
    // The two times are what the user has to compare, so they carry the weight of the block.
    highlight: {
        fontWeight: 700,
        color: theme.palette.tailwind.slate[900],
    },
}))

const selectMeetingServiceConfig = (data: MeetingServiceConfigurationResponse) => data.meetingServiceConfig
const selectDeadlineMessage = (data: DeadlineMessageResponse) => data.deadlineMessage

export type BookingMoveDeliverySyncDialogProps = Readonly<{
    open: boolean
    move: BookingMove
    /** The order carrying the deliveries — for a combined order that is the moved booking's own id. */
    meetingServiceOrderId: number
    userId: number
    currency: MonetaryAmount["currency"]
    /** Seats as saved on the order, for the orders whose form never rendered the seats field. */
    fallbackSeats: number
    priceList: SelectedPriceListItem
    suggestedDeliveries: DeliveryTimesField
    currentDeliveries: DeliveryTimesField
    onResolved: () => void
}>

export function BookingMoveDeliverySyncDialog({
    open,
    move,
    meetingServiceOrderId,
    userId,
    currency,
    fallbackSeats,
    priceList,
    suggestedDeliveries,
    currentDeliveries,
    onResolved,
}: BookingMoveDeliverySyncDialogProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const { user } = useCreateOrderFormConfig()
    const { isSpecialOrder } = useCreateOrderDialogType()
    const canSetProductWishPrice = useCanSetProductWishPrice()

    // The main order form, which has to follow whatever we persist here.
    const { setFieldValue, values } = useFormikContext<DynamicMainFormValues>()
    const seats = Number(values[DynamicConfigKey.NUMBER_OF_SEATS]) || fallbackSeats

    // The dialog's own buttons live outside the form, so the submit is triggered through the ref.
    const formikRef = useRef<FormikProps<CreateUpdateDeliveryFormValues>>(null)

    // Held between submit and save: the deadline check sits in between, and the user may have
    // edited the suggestion before submitting.
    const [pendingDeliveries, setPendingDeliveries] = useState<DeliveryTimesField | null>(null)
    const hasSavedRef = useRef(false)
    // What went to the server, written into the main form only once the save lands — writing it
    // earlier would leave the catering tab holding times the order never got.
    const savedDeliveriesRef = useRef<DeliveryTimesField | null>(null)

    const allowOutsideBusinessHours = user.userType === UserType.MERCHANT
    const allowPastDates =
        user.userType === UserType.SUPPORT || user.userType === UserType.MERCHANT || user.userType === UserType.SUPPLIER

    // Until this lands `allowedWeekdays` is empty, and an empty list rejects every date — so the
    // form waits for it rather than opening with a date picker that refuses the whole calendar.
    const { data: meetingServiceConfig, isInitialLoading: isConfigLoading } = useApiQuery(
        fetchMeetingServiceConfiguration,
        {
            queryName: ConfigQuery.MEETING_SERVICE_CONFIG,
            dependencies: { userId, merchantId: priceList.merchantId },
            select: selectMeetingServiceConfig,
            enabled: open,
        },
    )

    // Closing dates always reach the picker: whoever may override them is warned, not hidden from.
    const closingDates = meetingServiceConfig?.closingDates ?? []
    const openingHours = allowOutsideBusinessHours ? [] : (meetingServiceConfig?.deliveryTimes ?? [])
    const allowedWeekdays = allowOutsideBusinessHours
        ? DAY_OF_WEEK_NUMBER
        : openingHours.map((openingHour) => openingHour.dayOfWeek)

    const pendingEarliestTime = pendingDeliveries ? earliestDeliveryTime(pendingDeliveries.deliveryTimes) : null
    const hasProductWish = (pendingDeliveries ?? suggestedDeliveries).deliveryTimes.some((delivery) =>
        delivery.orderLines.some((orderLine) => isProductWishOrderLine(orderLine)),
    )

    const {
        data: deadlineMessage,
        isInitialLoading: isCheckingDeadline,
        isError: hasDeadlineCheckFailed,
    } = useApiQuery(fetchDeadlineMessage, {
        queryName: DeadlineMessageQuery.DELIVERY_DEADLINE_MESSAGE,
        dependencies: {
            merchantId: priceList.merchantId,
            deliveryTime: `${pendingDeliveries?.deliveryDate ?? ""}T${pendingEarliestTime ?? ""}`,
            productWish: hasProductWish,
            priceListId: priceList.priceListId,
            shopId: priceList.shopId,
        },
        select: selectDeadlineMessage,
        enabled: !isSpecialOrder && !!pendingDeliveries?.deliveryDate && !!pendingEarliestTime,
    })

    const { mutate: callUpdatePartialOrder, isLoading: isSaving } = useApiMutation(updatePartialMeetingServiceOrder, {
        async onSuccess(_params, _data, context) {
            // The catering tab reads this back on its own save, so it has to follow what we persisted.
            if (savedDeliveriesRef.current) {
                setFieldValue(DynamicConfigKey.MULTIPLE_DELIVERIES, savedDeliveriesRef.current)
            }
            await context.queryClient.invalidateQueries({
                queryKey: [ServiceCenterHomeQuery.SERVICE_CENTER_MEETINGS],
            })
            await context.queryClient.invalidateQueries({
                queryKey: [ServiceCenterUpcomingMeetingsQuery.UPCOMING_MEETINGS],
            })
            // The production list is filtered by delivery date, which is exactly what just moved.
            await context.queryClient.invalidateQueries({
                queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
            })
            await context.queryClient.invalidateQueries({ queryKey: [MeetingServiceQuery.ORDER_DETAILS] })
            onResolved()
        },
        // Re-throw so the API error still surfaces as the standard snackbar
        async onError(_params, error) {
            hasSavedRef.current = false
            setPendingDeliveries(null)
            throw error
        },
    })

    const save = useCallback(
        (deliveries: DeliveryTimesField) => {
            // The deadline answer can land on a later render than the submit, so the guard keeps
            // the save to exactly one call per submission.
            if (hasSavedRef.current) return
            hasSavedRef.current = true

            savedDeliveriesRef.current = deliveries
            callUpdatePartialOrder({
                userId,
                orderId: meetingServiceOrderId,
                deliveries: toPartialDeliveries(deliveries, seats, currency, dateTimeFactory),
            })
        },
        [callUpdatePartialOrder, userId, meetingServiceOrderId, seats, currency, dateTimeFactory],
    )

    // The deadline query only starts once a submission is pending, so a confirmed answer means "go";
    // an unconfirmed one hands the decision to `DeadlineMessageDialog`.
    const isDeadlineBlocking = !!pendingDeliveries && !isSpecialOrder && deadlineMessage?.isConfirmed === false

    useEffect(() => {
        if (!pendingDeliveries) return
        // A failed check must not strand the prompt: the deadline is advisory here — when it does
        // report a passed deadline the user may order anyway — and `/partial` is the real authority.
        if (isSpecialOrder || deadlineMessage?.isConfirmed || hasDeadlineCheckFailed) save(pendingDeliveries)
    }, [pendingDeliveries, isSpecialOrder, deadlineMessage?.isConfirmed, hasDeadlineCheckFailed, save])

    const handleSubmit = (formValues: CreateUpdateDeliveryFormValues) => {
        const deliveryDate = formValues.deliveryDate ? dateTimeFactory.toDate(formValues.deliveryDate) : null
        if (!deliveryDate) return

        const deliveryTimes = sortedDeliveryTimes(
            formValues.deliveryTimes.map((entry) => ({
                time: entry.time ? dateTimeFactory.toHourMinute(entry.time) : "",
                orderLines: entry.orderLines,
            })),
        )

        setPendingDeliveries({ deliveryDate, deliveryTimes, activeTime: earliestDeliveryTime(deliveryTimes) })
    }

    const currentDeliveryDate = currentDeliveries.deliveryDate
        ? dateTimeFactory.formatLongDate(dateTimeFactory.fromISO(currentDeliveries.deliveryDate))
        : ""
    const currentDeliveryTimes = formatDeliverySummary(currentDeliveries, dateTimeFactory)

    return (
        <>
            <ConfirmModal
                open={open}
                onClose={onResolved}
                title={l10n.getString("create-order-booking-move-delivery-sync-dialog-title")}
                buttonLabelCancel={l10n.getString("create-order-booking-move-delivery-sync-dialog-skip-button")}
                buttonLabelSubmit={l10n.getString("create-order-booking-move-delivery-sync-dialog-submit-button")}
                buttonColorSubmit="black"
                // The deadline check runs before the save, so the button has to cover both.
                buttonSubmitLoading={isSaving || isCheckingDeadline}
                buttonSubmitDisabled={isConfigLoading}
                autoCloseAfterSubmit={false}
                handleClick={() => formikRef.current?.submitForm()}
            >
                <Stack gap="1.5rem" className={classes.content}>
                    <Stack gap=".25rem" className={classes.summary}>
                        <LocalizedStrict
                            id="create-order-booking-move-delivery-sync-dialog-description"
                            vars={{
                                bookingDate: dateTimeFactory.formatLongDate(move.newStart),
                                bookingTime: dateTimeFactory.formatTime(move.newStart),
                            }}
                            elems={{ highlight: <span className={classes.highlight} /> }}
                        >
                            <Typography variant="body2">
                                The booking was moved to{" "}
                                <span className={classes.highlight}>
                                    {dateTimeFactory.formatLongDate(move.newStart)} at{" "}
                                    {dateTimeFactory.formatTime(move.newStart)}
                                </span>
                                .
                            </Typography>
                        </LocalizedStrict>
                        <LocalizedStrict
                            id="create-order-booking-move-delivery-sync-dialog-current-delivery"
                            vars={{ deliveryDate: currentDeliveryDate, deliveryTimes: currentDeliveryTimes }}
                            elems={{ highlight: <span className={classes.highlight} /> }}
                        >
                            <Typography variant="body2">
                                The meeting service is still on{" "}
                                <span className={classes.highlight}>
                                    {currentDeliveryDate} at {currentDeliveryTimes}
                                </span>
                                .
                            </Typography>
                        </LocalizedStrict>
                        <LocalizedStrict id="create-order-booking-move-delivery-sync-dialog-suggestion-hint">
                            <Typography variant="body2">
                                The suggestion below moves the delivery by the same amount. Accept it, or adjust the
                                date and time.
                            </Typography>
                        </LocalizedStrict>
                    </Stack>

                    {isConfigLoading ? (
                        <Stack gap="1.25rem">
                            <Skeleton variant="rounded" height="3.5rem" />
                            <Skeleton variant="rounded" height="3.5rem" />
                        </Stack>
                    ) : (
                        // No `enableReinitialize`: the suggestion is fixed for the life of the
                        // prompt, and reinitialising would discard what the user just typed.
                        <Formik<CreateUpdateDeliveryFormValues>
                            innerRef={formikRef}
                            initialValues={prepareInitialValues(
                                dateTimeFactory,
                                move.newStart,
                                openingHours,
                                suggestedDeliveries,
                            )}
                            validationSchema={buildDeliveryTimesSchema({
                                l10n,
                                dateTimeFactory,
                                allowedWeekdays,
                                closingDates,
                                canOverrideClosingDates: canOverrideClosingDates(user.userType),
                                openingHours,
                                allowPastDates,
                            })}
                            onSubmit={handleSubmit}
                        >
                            <Form>
                                <DeliveryTimeFields
                                    priceList={priceList}
                                    allowedWeekdays={allowedWeekdays}
                                    eventStartTime={move.newStart}
                                    closingDates={closingDates}
                                    canOverrideClosingDates={canOverrideClosingDates(user.userType)}
                                    openingHours={openingHours}
                                    maxAllowedDeliveries={suggestedDeliveries.deliveryTimes.length}
                                    allowPastDates={allowPastDates}
                                    // This prompt only retimes what was ordered; adding or dropping
                                    // a slot would change the order lines with it.
                                    isDeliveryCountLocked
                                />
                            </Form>
                        </Formik>
                    )}
                </Stack>
            </ConfirmModal>

            {isDeadlineBlocking && deadlineMessage && pendingDeliveries && (
                <DeadlineMessageDialog
                    message={deadlineMessage.message}
                    isStaffProductWish={hasProductWish && canSetProductWishPrice}
                    onClose={() => setPendingDeliveries(null)}
                    handlePlaceOrder={() => save(pendingDeliveries)}
                />
            )}
        </>
    )
}
